import { IconMinus, IconPlus } from '@tabler/icons-react-native';
import { Column, Row } from 'app/design/primitives/box';
import { Input } from 'app/design/primitives/input';
import { Modal } from 'app/design/primitives/modal';
import { Text } from 'app/design/primitives/text';
import type { useScopedTranslation } from 'app/hooks/i18n';
import { Button } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import type React from 'react';
import { Pressable, View } from 'react-native';
import Toast from 'react-native-toast-message';
import type { useListingBulkActions } from '../my-listings';
import { theme } from './shared';

export function ListingBulkEditDialogs({
  actions,
  selectedRowsState,
  t,
}: {
  actions: ReturnType<typeof useListingBulkActions>;
  selectedRowsState: [string[], React.Dispatch<React.SetStateAction<string[]>>];
  t: ReturnType<typeof useScopedTranslation>['t'];
}) {
  const {
    showDuplicateModal,
    setShowDuplicateModal,
    showPriceModal,
    setShowPriceModal,
    duplicateCount,
    setDuplicateCount,
    price,
    setPrice,
    isDuplicatingListings,
    duplicateListings,
    updateListingsPrice,
    finishBulkEdit,
    alert,
    hideAlert,
  } = actions;
  return (
    <>
      <Modal onClose={() => setShowDuplicateModal(false)} open={showDuplicateModal} transparent>
        <Modal.Card gap="spacing.general.x6">
          <Modal.Title allowClose onClose={() => setShowDuplicateModal(false)}>
            {t('duplicateModal.title')}
          </Modal.Title>
          <Row alignItems="center" justifyContent="space-between" paddingHorizontal="spacing.general.x2">
            <Text fontWeight="700" variant="bodyBase">
              {t('duplicateModal.copies')}
            </Text>
            <Row
              alignItems="center"
              borderColor="border.secondary"
              borderRadius="radius.lg"
              borderWidth={1}
              gap="spacing.general.x4"
              padding="spacing.general.x2"
            >
              <Pressable
                disabled={duplicateCount <= 1}
                onPress={() => setDuplicateCount((value) => Math.max(1, value - 1))}
              >
                <IconMinus color={theme.colors['icon.primary']} size={20} strokeWidth={ICON_STROKE_WIDTH} />
              </Pressable>
              <Text variant="bodyBase">{duplicateCount}</Text>
              <Pressable
                disabled={duplicateCount >= 99}
                onPress={() => setDuplicateCount((value) => Math.min(99, value + 1))}
              >
                <IconPlus color={theme.colors['icon.primary']} size={20} strokeWidth={ICON_STROKE_WIDTH} />
              </Pressable>
            </Row>
          </Row>
          <Row gap="spacing.general.x4" justifyContent="flex-end">
            <Button label={t('cancelButton')} onPress={() => setShowDuplicateModal(false)} variant="secondaryNeutral" />
            <Button
              disabled={isDuplicatingListings}
              label={t('duplicateModal.confirm')}
              onPress={() =>
                duplicateListings(
                  { listingIds: selectedRowsState[0], count: duplicateCount },
                  {
                    onSuccess: () => {
                      setShowDuplicateModal(false);
                      finishBulkEdit();
                      Toast.show({
                        type: 'success',
                        position: 'bottom',
                        bottomOffset: 100,
                        text1: t('duplicateToast.title'),
                        text2: t('duplicateToast.text'),
                        visibilityTime: 5000,
                      });
                    },
                  },
                )
              }
              variant="primary"
            />
          </Row>
        </Modal.Card>
      </Modal>

      <Modal onClose={() => setShowPriceModal(false)} open={showPriceModal} transparent>
        <Modal.Card gap="spacing.general.x6">
          <Modal.Title allowClose onClose={() => setShowPriceModal(false)}>
            {t('priceModal.title', { count: selectedRowsState[0].length })}
          </Modal.Title>
          <Input
            label={t('priceModal.label')}
            markAsRequired
            onChangeText={setPrice}
            placeholder={t('priceModal.placeholder')}
            value={price}
          />
          <Row gap="spacing.general.x4" justifyContent="flex-end">
            <Button label={t('cancelButton')} onPress={() => setShowPriceModal(false)} variant="secondaryNeutral" />
            <Button
              disabled={!price.trim()}
              label={t('priceModal.save')}
              onPress={() =>
                updateListingsPrice(
                  { listingIds: selectedRowsState[0], price: price.trim() },
                  {
                    onSuccess: () => {
                      setShowPriceModal(false);
                      finishBulkEdit();
                      Toast.show({
                        type: 'success',
                        position: 'bottom',
                        bottomOffset: 100,
                        text1: t('priceUpdatedToast.title'),
                        text2: t('priceUpdatedToast.text'),
                        visibilityTime: 5000,
                      });
                    },
                  },
                )
              }
              variant="primary"
            />
          </Row>
        </Modal.Card>
      </Modal>

      <Modal onClose={hideAlert} open={Boolean(alert?.visible)} transparent>
        <Modal.Card>
          <Modal.Title>{alert?.title}</Modal.Title>

          <Column paddingHorizontal="spacing.general.x6">
            <Text variant="bodyBase">{alert?.message}</Text>
          </Column>

          <Row gap="spacing.general.x4" width="100%">
            {alert?.buttons?.map((button) => (
              <View key={JSON.stringify(button)} style={{ flex: 1 }}>
                <Button
                  label={button.text}
                  onPress={() => {
                    hideAlert();
                    button.onPress?.();
                  }}
                  variant={
                    button.style === 'destructive' ? 'destructive' : button.isPreferred ? 'primary' : 'secondaryNeutral'
                  }
                />
              </View>
            ))}
          </Row>
        </Modal.Card>
      </Modal>
    </>
  );
}
